import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { registerPasskey, passkeySupported, inIframe } from "@/lib/passkey";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import BusinessCardForm from "@/components/BusinessCardForm";
import BusinessCardModal from "@/components/BusinessCardModal";
import EmailSignature from "@/components/EmailSignature";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [passkeys, setPasskeys] = useState([]);
  const [nickname, setNickname] = useState("");
  const [adding, setAdding] = useState(false);
  const [enforcePasskey, setEnforcePasskey] = useState(false);
  const [savingEnforce, setSavingEnforce] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/auth/passkeys").then((r) => setPasskeys(r.data));
  useEffect(() => {
    load();
    if (isAdmin) api.get("/settings").then((r) => setEnforcePasskey(!!r.data.enforce_passkey_enrollment)).catch(() => {});
  }, [isAdmin]);

  const toggleEnforce = async (val) => {
    setSavingEnforce(true);
    try {
      const { data } = await api.patch("/admin/settings", { enforce_passkey_enrollment: val });
      setEnforcePasskey(!!data.enforce_passkey_enrollment);
      await refresh();
      toast.success(val ? "All users must now have a passkey" : "Passkey requirement disabled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update setting");
    } finally {
      setSavingEnforce(false);
    }
  };

  const add = async () => {
    if (inIframe()) {
      toast.error("Passkeys can't be created inside the embedded preview. Open the app in its own browser tab first.");
      return;
    }
    if (!passkeySupported()) return toast.error("This device/browser does not support passkeys.");
    setAdding(true);
    try {
      await registerPasskey(nickname || "My passkey");
      toast.success("Passkey added");
      setNickname("");
      load();
    } catch (err) {
      if (err.name === "NotAllowedError") toast.error("Passkey setup was cancelled or timed out.");
      else if (err.name === "SecurityError") toast.error("Passkeys require the app to be opened on its own secure (HTTPS) domain.");
      else toast.error(formatApiErrorDetail(err.response?.data?.detail || err.message) || "Could not add passkey");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/auth/passkeys/${id}`);
    toast.success("Passkey removed");
    load();
  };

  return (
    <div className="p-6 sm:p-8 max-w-3xl">
      <header className="mb-8">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-400">Account</p>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight mt-1">Settings</h1>
      </header>

      <section className="bg-white border border-gray-200 rounded-md mb-6">
        <div className="px-5 py-4 border-b border-gray-100"><h3 className="font-heading font-semibold">Profile</h3></div>
        <div className="divide-y divide-gray-50">
          <Row label="Name" value={user?.name} />
          <Row label="Email" value={user?.email} />
          <div className="px-5 py-3 flex items-center justify-between">
            <span className="text-sm text-gray-500">Role</span>
            <Badge variant="outline" className="capitalize">{user?.role}</Badge>
          </div>
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-md mb-6" data-testid="business-card-section">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Icons.IdCard className="w-4 h-4 text-[#1B4332]" />
            <h3 className="font-heading font-semibold">Digital business card</h3>
          </div>
          <Button variant="outline" size="sm" onClick={() => setCardOpen(true)} data-testid="preview-card-btn">
            <Icons.QrCode className="w-4 h-4 mr-1.5" /> Preview &amp; share
          </Button>
        </div>
        <div className="p-5">
          <p className="text-sm text-gray-500 mb-5">
            Fill in your contact details. A personal QR code is generated so anyone can scan and save your details instantly.
          </p>
          <BusinessCardForm
            initial={user?.profile || {}}
            saveFn={(payload) => api.patch("/auth/profile", payload)}
            onSaved={() => refresh()}
          />

          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-1">
              <Icons.Mail className="w-4 h-4 text-[#1B4332]" />
              <h4 className="font-heading font-semibold text-sm">Email signature</h4>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Copy a ready-made signature (with your QR code) into Gmail, Outlook or Apple Mail settings.
            </p>
            <EmailSignature name={user?.name} email={user?.email} profile={user?.profile || {}} />
          </div>
        </div>
      </section>

      <BusinessCardModal
        open={cardOpen}
        onOpenChange={setCardOpen}
        name={user?.name}
        email={user?.email}
        role={user?.role}
        profile={user?.profile || {}}
      />

      <section className="bg-white border border-gray-200 rounded-md">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <Icons.Fingerprint className="w-4 h-4 text-[#1B4332]" />
          <h3 className="font-heading font-semibold">Passkeys</h3>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-500">
            Add a passkey (Touch ID, Face ID, Windows Hello or a security key) to sign in without a password.
          </p>
          {inIframe() && (
            <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3" data-testid="passkey-iframe-warning">
              <Icons.TriangleAlert className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-800">
                Passkeys can't be created inside this embedded preview. Open the app in its own browser tab, then add your passkey.
                <button
                  onClick={() => window.open(window.location.href, "_blank", "noopener")}
                  className="ml-1 font-semibold underline hover:no-underline"
                  data-testid="open-new-tab-btn"
                >
                  Open in a new tab
                </button>
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={nickname} onChange={(e) => setNickname(e.target.value)}
              placeholder="Passkey name (e.g. MacBook Touch ID)" data-testid="passkey-nickname" />
            <Button onClick={add} disabled={adding} data-testid="add-passkey-btn" className="bg-[#0A0A0A] hover:bg-gray-800 shrink-0">
              <Icons.Plus className="w-4 h-4 mr-1.5" />{adding ? "Waiting…" : "Add passkey"}
            </Button>
          </div>

          <div className="divide-y divide-gray-50 border-t border-gray-100">
            {passkeys.length === 0 && <div className="py-6 text-center text-sm text-gray-400">No passkeys yet.</div>}
            {passkeys.map((p) => (
              <div key={p.id} className="py-3 flex items-center justify-between" data-testid={`passkey-${p.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-md bg-gray-100 flex items-center justify-center">
                    <Icons.KeyRound className="w-4 h-4 text-gray-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">{p.nickname}</div>
                    <div className="text-[11px] text-gray-400">Added {new Date(p.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50"
                  onClick={() => remove(p.id)} data-testid={`remove-passkey-${p.id}`}>
                  <Icons.Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {isAdmin && (
        <section className="bg-white border border-gray-200 rounded-md mt-6" data-testid="admin-security-section">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
            <Icons.ShieldCheck className="w-4 h-4 text-[#1B4332]" />
            <h3 className="font-heading font-semibold">Organization security</h3>
          </div>
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-medium">Require a passkey for all users</div>
              <p className="text-[12px] text-gray-500 mt-0.5">
                When on, every user (including admins) must set up a passkey before using the app, and the app re-locks with a passkey after being idle/offline.
              </p>
            </div>
            <Switch checked={enforcePasskey} disabled={savingEnforce} onCheckedChange={toggleEnforce} data-testid="enforce-passkey-switch" />
          </div>
        </section>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="px-5 py-3 flex items-center justify-between">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}
